import { formatNumber as formatNumberIntl } from '@repletoryreach/sdk-utils/format';
import { useT, type Translate } from '../i18n/I18nProvider';

/**
 * The customer's unit is the credit: one credit is one rand of messaging at
 * the listed rates, held in the wallet as minor units (100 per credit).
 * Everything the console shows about the wallet, usage and prices is in
 * credits; the R symbol is kept for money actually paid (top-ups, plan fees,
 * invoices). Owner's ruling, 6 September 2026.
 */
export const MINOR_PER_CREDIT = 100;

/**
 * The bare figure, "1,18", for a column or a card whose heading already says
 * credits: five plans side by side have no room for the word in every cell.
 */
export function formatCreditAmount(minor: number): string {
  const value = Math.round(minor) / MINOR_PER_CREDIT;
  return formatNumberIntl(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** "4 999,29 credits", "1 credit", in the tenant's locale. The currency argument is accepted and ignored so call sites read like the money formatter they replaced. */
export function formatCredits(minor: number, t: Translate, _currency?: string): string {
  const unit = Math.abs(minor) === MINOR_PER_CREDIT ? t('billing.credits.unit.one') : t('billing.credits.unit.other');
  return `${formatCreditAmount(minor)} ${unit}`;
}

/** The formatter bound to the current locale's words for the unit. */
export function useCredits(): (minor: number, currency?: string) => string {
  const t = useT();
  return (minor, currency) => formatCredits(minor, t, currency);
}
